/* @HEADER
 * @version 1.0.1598 | 2026-09-19
 * @purpose The one decision App.tsx made about authentication: a Wave 5 session envelope on disk means the JWT-backed AuthPort, otherwise the legacy single-user adapter. Pure `hasJwtSession` for the decision, `createAuthAdapterForSession` for the wiring — moved out of the host so the choice is testable and App.tsx is assembly only (ZVX-DEV-6131).
 * @sidecar chooseAuthAdapter.ts.header.md
 * @layer app | @hex adapter | @ctx auth
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-6131, ZVX-DEV-1349, ZVX-DEV-1106

import type { AuthPort } from './AuthContext'
import { createJwtAuthAdapter } from './JwtAuthAdapter'
import { createZvenixLocalAuthAdapter, type UserListSource } from './ZvenixLocalAuthAdapter'
import { AUTH_STORAGE_KEYS } from '../features/auth/verifyEmailApi'

type StorageLike = Pick<Storage, 'getItem'>

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * Wave 1.A → Wave 5 BBA seam (ZVX-DEV-1106, ZVX-DEV-1349). A session envelope
 * (`zvenix.auth.localUserIdentity`) on disk is the whole evidence: with it the
 * HTTP/WS clients must carry the access JWT and the soft-relogin banner must
 * read pending state; without it a pre-Wave-5 session keeps working unchanged
 * until the person registers or logs in. A storage that throws (private
 * window, site data blocked) reads as «no session» — the legacy adapter
 * needs nothing from disk to start.
 */
export function hasJwtSession(storage: StorageLike | null = defaultStorage()): boolean {
  if (!storage) return false
  try {
    return storage.getItem(AUTH_STORAGE_KEYS.localUserIdentity) !== null
  } catch {
    return false
  }
}

export interface CreateAuthAdapterForSessionOptions {
  /** The legacy adapter's user list (the active database's users). */
  userListSource: UserListSource
  storage?: StorageLike | null
}

/**
 * The adapter for THIS mount. A one-shot: a later login resets the page
 * through `window.location.hash` and re-mounts the authenticated app, which
 * calls this again and picks up the JWT adapter.
 */
export function createAuthAdapterForSession(options: CreateAuthAdapterForSessionOptions): AuthPort {
  const storage = options.storage === undefined ? defaultStorage() : options.storage
  if (hasJwtSession(storage)) {
    return createJwtAuthAdapter() as unknown as AuthPort
  }
  return createZvenixLocalAuthAdapter({ userListSource: options.userListSource }) as unknown as AuthPort
}
